import { z } from 'zod';
import { WEBSITE_SCHEMES, WEBSITE_SECTION_TYPES } from '@bulk/types';
import { idSchema } from './common';
import { linkSchema, localizedText } from './website-blocks';
import { memberAreaDesignSchema } from './member-home';

export const DESIGN_PRESETS = ['classic', 'bold', 'legacy', 'minimal'] as const;
export type DesignPreset = (typeof DESIGN_PRESETS)[number];

/** Families the site may load from Google Fonts. A closed list, so a design can never smuggle CSS into the stylesheet URL. */
export const GOOGLE_FONTS = [
  'Schibsted Grotesk',
  'Inter',
  'Manrope',
  'Space Grotesk',
  'Montserrat',
  'Archivo',
  'Archivo Black',
  'Anton',
  'Bebas Neue',
  'Oswald',
  'Barlow Condensed',
  'Roboto Condensed',
  'Playfair Display',
  'Bodoni Moda',
  'DM Serif Display',
  'Abril Fatface',
  'Noto Sans Georgian',
  'Noto Serif Georgian',
] as const;

/** The weights each family has on Google Fonts; asking for one it lacks makes Google refuse the whole stylesheet. */
export const GOOGLE_FONT_WEIGHTS: Record<(typeof GOOGLE_FONTS)[number], readonly number[]> = {
  'Schibsted Grotesk': [400, 700, 900],
  Inter: [400, 600, 700, 900],
  Manrope: [400, 600, 700, 800],
  'Space Grotesk': [400, 500, 700],
  Montserrat: [400, 600, 700, 900],
  Archivo: [400, 600, 700, 900],
  'Archivo Black': [400],
  Anton: [400],
  'Bebas Neue': [400],
  Oswald: [400, 600, 700],
  'Barlow Condensed': [400, 600, 700, 900],
  'Roboto Condensed': [400, 700, 900],
  'Playfair Display': [400, 700, 900],
  'Bodoni Moda': [400, 700, 900],
  'DM Serif Display': [400],
  'Abril Fatface': [400],
  'Noto Sans Georgian': [400, 700, 900],
  'Noto Serif Georgian': [400, 700, 900],
};

export const SYSTEM_FONTS = ['sans', 'serif', 'condensed', 'mono'] as const;

export const fontRefSchema = z.discriminatedUnion('source', [
  z.object({ source: z.literal('system'), family: z.enum(SYSTEM_FONTS) }),
  z.object({ source: z.literal('google'), family: z.enum(GOOGLE_FONTS) }),
  z.object({
    source: z.literal('custom'),
    family: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9 _-]{1,60}$/, 'Letters, digits, spaces, dashes and underscores')
      .describe('A name for people; the stylesheet uses a name made from the asset id'),
    regularAssetId: idSchema,
    boldAssetId: idSchema.nullable().default(null),
  }),
]);
export type FontRef = z.infer<typeof fontRefSchema>;

export const fontRoleSchema = z.object({
  latin: fontRefSchema,
  georgian: fontRefSchema.nullable().default(null).describe('Used for Georgian text when the Latin font has no Georgian letters'),
});
export type FontRole = z.infer<typeof fontRoleSchema>;

export const COLOR_TOKENS = ['primary', 'secondary', 'background', 'text', 'surface', 'onPrimary', 'onSecondary', 'white', 'black'] as const;
export type ColorToken = (typeof COLOR_TOKENS)[number];

export const colorRefSchema = z.union([z.enum(COLOR_TOKENS), z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A token or a #rrggbb colour')]);
export type ColorRef = z.infer<typeof colorRefSchema>;

export const schemeKeySchema = z.string().trim().regex(/^[a-z0-9][a-z0-9-]{0,39}$/);

export const schemeDefSchema = z.object({
  label: localizedText.default({}),
  background: colorRefSchema,
  text: colorRefSchema,
  accent: colorRefSchema,
});
export type SchemeDef = z.infer<typeof schemeDefSchema>;

const nullableCase = z.enum(['uppercase', 'none']).nullable().default(null).describe('null leaves each block as it is drawn');

/** The site's own pages the header can list before the gym's pages. */
export const SITE_NAV_LINKS = ['home', 'plans', 'classes', 'trainers', 'contact'] as const;
export type SiteNavLink = (typeof SITE_NAV_LINKS)[number];

export const SITE_MOTIONS = ['none', 'lively'] as const;
export const SCROLLBAR_STYLES = ['brand', 'quiet', 'browser'] as const;
export const FIELD_STYLES = ['outline', 'filled', 'line'] as const;
export const UI_SCALES = [1, 1.125, 1.25, 1.375] as const;
export type UiScale = (typeof UI_SCALES)[number];
export type SiteMotion = (typeof SITE_MOTIONS)[number];
export type ScrollbarStyle = (typeof SCROLLBAR_STYLES)[number];
export type FieldStyle = (typeof FIELD_STYLES)[number];

export const siteDesignSchema = z.object({
  version: z.literal(1).default(1),
  preset: z.enum(DESIGN_PRESETS).default('classic'),
  typography: z
    .object({
      heading: fontRoleSchema.nullable().default(null),
      body: fontRoleSchema.nullable().default(null),
      ui: fontRoleSchema.nullable().default(null).describe('Navigation, buttons and small labels'),
      headingCase: nullableCase,
      headingWeight: z.number().int().min(100).max(900).multipleOf(100).nullable().default(null),
      headingTracking: z.number().min(-0.1).max(0.5).nullable().default(null).describe('In em'),
      uiCase: nullableCase,
      uiTracking: z.number().min(0).max(0.5).nullable().default(null),
      uiScale: z.literal(UI_SCALES).default(1).describe('Multiplies the size of text set in the UI face (buttons, menus, labels), for a face that is narrow at its usual size'),
      uiBold: z.boolean().default(false).describe('Draws text set in the UI face heavier: weight 700 where the face has a bold file, else a thin outline in its own colour on Latin letters; Georgian letters are left as drawn'),
    })
    .default(() => ({ heading: null, body: null, ui: null, headingCase: null, headingWeight: null, headingTracking: null, uiCase: null, uiTracking: null, uiScale: 1 as const, uiBold: false })),
  shape: z
    .object({
      radius: z.enum(['none', 'sm', 'md', 'lg', 'full']).nullable().default(null),
      button: z.enum(['solid', 'outline', 'pill', 'block']).nullable().default(null),
    })
    .default(() => ({ radius: null, button: null })),
  schemes: z
    .record(schemeKeySchema, schemeDefSchema)
    .refine((schemes) => Object.keys(schemes).length <= 16, 'At most 16 colour schemes')
    .default({})
    .describe('Overrides the built-in schemes by key, and adds new ones'),
  header: z
    .object({
      variant: z.enum(['bar', 'centered', 'transparent']).default('bar'),
      scheme: schemeKeySchema.default('default'),
      sticky: z.boolean().default(true),
      showLanguage: z.boolean().default(true),
      cta: linkSchema.nullable().default(null),
      links: z
        .array(z.enum(SITE_NAV_LINKS))
        .nullable()
        .default(null)
        .describe("The site's own pages the menu lists before the gym's pages; null lists them all. A page left out still works and stays in the footer"),
    })
    .default(() => ({ variant: 'bar' as const, scheme: 'default', sticky: true, showLanguage: true, cta: null, links: null })),
  footer: z
    .object({
      variant: z.enum(['columns', 'simple', 'headline']).default('columns'),
      scheme: schemeKeySchema.default('default'),
      headline: localizedText.default({}),
    })
    .default(() => ({ variant: 'columns' as const, scheme: 'default', headline: {} })),
  blockDefaults: z
    .partialRecord(z.enum(WEBSITE_SECTION_TYPES), z.object({ variant: z.string().trim().max(40).optional(), scheme: schemeKeySchema.optional() }))
    .default({})
    .describe('The look and colours a new block of each type starts with'),
  allowedBlocks: z.array(z.enum(WEBSITE_SECTION_TYPES)).default([]).describe('The blocks the library offers; empty offers every block'),
  motion: z
    .enum(SITE_MOTIONS)
    .default('none')
    .describe('lively: the cover arrives in sequence and its picture drifts, sections and cards rise into view, and buttons and cards answer the pointer, for every visitor unless respectReducedMotion is on'),
  respectReducedMotion: z
    .boolean()
    .default(false)
    .describe('On: a visitor whose device asks for reduced motion gets the lively reveals as soft fades, colour changes on hover and press but no lifts or scales, and instant carousel moves; off: everyone sees the full motion'),
  controls: z
    .object({
      scrollbar: z.enum(SCROLLBAR_STYLES).default('brand').describe("brand: the brand colour on the scheme's background; quiet: the text colour, faint; browser: the browser's own"),
      field: z.enum(FIELD_STYLES).default('outline').describe('How inputs, selects and text areas on the site are drawn'),
    })
    .default(() => ({ scrollbar: 'brand' as const, field: 'outline' as const }))
    .describe('Scrollbars and form fields, branded for every site'),
  customCss: z
    .string()
    .max(20_000)
    .refine((css) => !/<\/?style|<script|@import|javascript:|expression\s*\(|behavior\s*:/i.test(css), 'Custom CSS cannot close the style element, import other sheets or run script')
    .default(''),
  memberArea: memberAreaDesignSchema.default(() => memberAreaDesignSchema.parse({})).describe('The member home cards the content team may use, their default order, and the member header'),
});
export type SiteDesign = z.infer<typeof siteDesignSchema>;

/** A stored design that no longer parses draws the plain look rather than taking the site down. */
export function readDesign(raw: unknown): SiteDesign | null {
  if (raw === null || raw === undefined) return null;
  const parsed = siteDesignSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** The scheme keys a site offers: the built-in ones, then any the design adds. */
export function designSchemeKeys(design: SiteDesign | null): string[] {
  return [...new Set<string>([...WEBSITE_SCHEMES, ...Object.keys(design?.schemes ?? {})])];
}

/** Every face the design's heading, body and UI roles name, Latin before Georgian. */
export function designFonts(design: SiteDesign | null): FontRef[] {
  if (!design) return [];
  const { heading, body, ui } = design.typography;
  return [heading, body, ui].flatMap((role) => (role ? [role.latin, ...(role.georgian ? [role.georgian] : [])] : []));
}

/** Every font file a design points at, so the public payload can resolve them with the other media. */
export function designFontAssetIds(design: SiteDesign | null): string[] {
  return designFonts(design).flatMap((font) => (font.source === 'custom' ? [font.regularAssetId, ...(font.boldAssetId ? [font.boldAssetId] : [])] : []));
}

/** The one Google Fonts stylesheet for every Google family the design uses, or null when it uses none. */
export function googleFontsHref(design: SiteDesign | null): string | null {
  const families = [...new Set(designFonts(design).flatMap((f) => (f.source === 'google' ? [f.family] : [])))].sort();
  if (families.length === 0) return null;
  const query = families.map((family) => `family=${family.replace(/ /g, '+')}:wght@${GOOGLE_FONT_WEIGHTS[family].join(';')}`).join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}

const google = (family: (typeof GOOGLE_FONTS)[number], georgian: (typeof GOOGLE_FONTS)[number] | null = 'Noto Sans Georgian'): FontRole => ({
  latin: { source: 'google', family },
  georgian: georgian ? { source: 'google', family: georgian } : null,
});

/** A complete design to start from; every field stays editable afterwards. */
export function designPreset(preset: DesignPreset): SiteDesign {
  const base = siteDesignSchema.parse({ preset });
  switch (preset) {
    case 'classic':
      return base;
    case 'bold':
      return {
        ...base,
        typography: { ...base.typography, heading: google('Archivo Black'), ui: google('Archivo'), headingCase: 'uppercase', headingWeight: 900, headingTracking: -0.02, uiCase: 'uppercase', uiTracking: 0.06 },
        shape: { radius: 'sm', button: 'block' },
        schemes: { dark: { label: {}, background: 'black', text: 'white', accent: 'primary' } },
        header: { ...base.header, variant: 'bar', scheme: 'dark' },
        footer: { ...base.footer, variant: 'headline', scheme: 'dark' },
      };
    case 'legacy':
      return {
        ...base,
        typography: {
          heading: google('Bodoni Moda', 'Noto Serif Georgian'),
          body: google('Inter'),
          ui: google('Oswald'),
          headingCase: 'uppercase',
          headingWeight: 400,
          headingTracking: 0,
          uiCase: 'uppercase',
          uiTracking: 0.12,
          uiScale: 1,
          uiBold: false,
        },
        shape: { radius: 'none', button: 'block' },
        schemes: {
          default: { label: {}, background: 'black', text: 'white', accent: 'primary' },
          dark: { label: {}, background: '#111111', text: 'white', accent: 'primary' },
          brand: { label: {}, background: 'primary', text: 'black', accent: 'black' },
        },
        header: { variant: 'transparent', scheme: 'default', sticky: true, showLanguage: true, cta: { label: { en: 'Become a member', ka: 'გახდი წევრი' }, href: '/plans' }, links: null },
        footer: { variant: 'headline', scheme: 'default', headline: { en: 'Follow the legend', ka: 'გამოჰყევი ლეგენდას' } },
        blockDefaults: { HERO: { variant: 'centered' }, CTA: { variant: 'minimal' }, SOCIAL: { variant: 'headline' } },
      };
    case 'minimal':
      return {
        ...base,
        typography: { ...base.typography, heading: google('Manrope'), body: google('Manrope'), ui: google('Manrope'), headingCase: 'none', headingWeight: 600, headingTracking: -0.02, uiCase: 'none', uiTracking: 0 },
        shape: { radius: 'lg', button: 'pill' },
        schemes: {
          default: { label: {}, background: 'white', text: '#1a1a1a', accent: 'primary' },
          muted: { label: {}, background: '#f5f5f4', text: '#1a1a1a', accent: 'primary' },
        },
        header: { ...base.header, variant: 'centered' },
        footer: { ...base.footer, variant: 'simple' },
      };
  }
}

export const updateSiteDesignSchema = z.object({
  design: siteDesignSchema.nullable().describe('null goes back to the plain look'),
  branding: z
    .object({
      primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
      secondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
      backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
      textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    })
    .partial()
    .optional()
    .describe("The gym's four brand colours, which every scheme is made from"),
});
export type UpdateSiteDesignInput = z.infer<typeof updateSiteDesignSchema>;

export const siteDesignResponseSchema = z.object({
  design: siteDesignSchema.nullable(),
  fonts: z.array(z.object({ id: idSchema, fileName: z.string(), mimeType: z.string(), url: z.string() })).describe("The gym's uploaded font files"),
  updatedAt: z.string().nullable(),
});
export type SiteDesignResponse = z.infer<typeof siteDesignResponseSchema>;
